import { IndexedTsPoint } from '@toggle/design-system';
import { StoreApi } from 'zustand';

import { Indicator } from '~/api/timeseries/timeseries-schema';
import { getIndicatorData } from '~/api/timeseries/timeseries-service';

import { create } from '../create-store/createStore';
import {
  Data,
  formatIndicators,
  getNewIndicators,
  indicatorsType,
  initialState,
  Point,
  Records,
  ThresholdsDates,
} from './utils';

type State = {
  indicators: { data: Data; tsData: Records; thresholdsDates: ThresholdsDates };
  error: boolean;
  loading: boolean;
};

type Actions = {
  initialize: (activeId?: Indicator) => Promise<void>;
  getIndicator: (featureId: Indicator) => unknown;
  setData: (featureId: Indicator, data: IndexedTsPoint[]) => void;
  update: (activeId: Indicator) => void;
};

type IndicatorStore = State & Actions;

const getIndicator = async () => {
  const response = await Promise.all(
    indicatorsType.map(id => getIndicatorData(id))
  );
  if (!response) {
    return null;
  }
  const parsedResponse = indicatorsType.reduce(
    (res, id, i) => ({
      ...res,
      ...(response[i].error ? null : { [id]: response[i].data?.result.data }),
    }),
    {} as Record<Indicator, Point[]>
  );
  return parsedResponse;
};

const initialize = async (
  set: StoreApi<IndicatorStore>['setState'],
  activeId?: Indicator
) => {
  try {
    set({ loading: true });
    const response = await getIndicator();
    const formattedData = formatIndicators(response, activeId);

    set({ indicators: formattedData, error: false, loading: false });
  } catch (error) {
    set({ error: true, loading: false });
  }
};

export const useIndicatorStore = create<IndicatorStore>((set, get) => ({
  ...initialState,
  initialize: async (activeId?: Indicator) => initialize(set, activeId),
  setData: (featureId, data) => {
    set(prevState => ({
      indicators: { ...prevState.indicators, [featureId]: data },
    }));
  },
  update: (activeId: Indicator) => {
    const indicators = get().indicators;
    const newIndicators = getNewIndicators(
      activeId,
      indicators.data,
      indicators.tsData
    );
    set({ indicators: newIndicators });
  },
  getIndicator: async () => getIndicator(),
}));
